import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import SectionReveal from '../components/SectionReveal';

const values = [
  { title: 'Precision Over Packaging', desc: 'Every tool we build is purpose-engineered from scratch — no templates, no shortcuts.' },
  { title: 'Collaboration First', desc: 'We work alongside your operational and creative teams so the solution fits your reality, not a demo.' },
  { title: 'Relentless Iteration', desc: 'We don\'t just ship and vanish. Our solutions evolve as your workflows change.' },
  { title: 'Impact That Matters', desc: 'We measure success by the time we give back to people — not by feature count.' },
];

const About = () => (
  <>
    {/* ───── PAGE HERO ───── */}
    <section className="page-hero">
      <div className="container">
        <motion.p
          className="section__eyebrow"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          About Us
        </motion.p>
        <motion.h1
          className="page-hero__title"
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.1 }}
        >
          We Build AI That <span className="gradient-text">Actually Works</span>
        </motion.h1>
        <motion.p
          className="page-hero__sub"
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.25 }}
        >
          Full Speed AI was founded on a simple observation: the AI market is flooded with
          thousands of plug-and-play tools, but few that fit your team's exact needs.
        </motion.p>
      </div>
    </section>

    {/* ───── STORY ───── */}
    <section className="section">
      <div className="container about-story">
        <SectionReveal>
          <div className="about-story__block">
            <h2 className="section__title" style={{ textAlign: 'left' }}>The Problem</h2>
            <p className="about-story__text">
              Prepackaged software may offer surface-level solutions, but it rarely aligns with the
              way your team actually operates. That leads to friction, frustration, and missed opportunities.
            </p>
          </div>
        </SectionReveal>

        <SectionReveal delay={0.15}>
          <div className="about-story__block">
            <h2 className="section__title" style={{ textAlign: 'left' }}>Our Approach</h2>
            <p className="about-story__text">
              Full Speed AI takes a different approach. We build bespoke automation tools tailored to your specific
              organizational workflows — tools that eliminate repetitive manual work, integrate into your
              existing systems, and adapt as your processes evolve. We work closely with operational and creative teams
              to build modular, AI-powered solutions that actually move the needle.
            </p>
          </div>
        </SectionReveal>
      </div>
    </section>

    {/* ───── VALUES ───── */}
    <section className="section">
      <div className="container">
        <SectionReveal>
          <p className="section__eyebrow">How We Operate</p>
          <h2 className="section__title">Our Values</h2>
        </SectionReveal>

        <div className="values__grid">
          {values.map((v, i) => (
            <SectionReveal key={i} delay={i * 0.1}>
              <div className="card">
                <div className="card__number">{String(i + 1).padStart(2, '0')}</div>
                <h3 className="card__title">{v.title}</h3>
                <p className="card__desc">{v.desc}</p>
              </div>
            </SectionReveal>
          ))}
        </div>
      </div>
    </section>

    {/* ───── CTA ───── */}
    <section className="section cta">
      <div className="container">
        <SectionReveal>
          <div className="cta__box">
            <div className="cta__glow" />
            <h2 className="cta__title">Let's Build Something <span className="gradient-text">Together</span></h2>
            <p className="cta__sub">
              Whether you have a clear vision or just a workflow headache, we'd love to talk.
            </p>
            <Link to="/contact" className="btn btn--primary btn--lg">
              Book Your Dream Call
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="5" y1="12" x2="19" y2="12" /><polyline points="12 5 19 12 12 19" /></svg>
            </Link>
          </div>
        </SectionReveal>
      </div>
    </section>
  </>
);

export default About;
